import React, { useState, useEffect } from 'react';
import { Building, Plus, Trash2 } from 'lucide-react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import InventoryMachines from './InventoryMachines';
import InventoryConsumables from './InventoryConsumables';
import InventorySpareParts from './InventorySpareParts';
import InventoryDemoUnit from './InventoryDemoUnit';
import InventoryAccessories from './InventoryAccessories';

const Inventory = ({ axiosInstance }) => {
    // Tab State
    const [activeTab, setActiveTab] = useState('machines');

    // Warehouse State
    const [warehouses, setWarehouses] = useState([]);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);
    const [isAddWarehouseOpen, setIsAddWarehouseOpen] = useState(false);
    const [newWarehouse, setNewWarehouse] = useState({
        name: '',
        person_in_charge: '',
        contact_number: ''
    });

    // Fetch Warehouses
    useEffect(() => {
        if (axiosInstance) {
            fetchWarehouses();
        }
    }, [axiosInstance]);

    const fetchWarehouses = async () => {
        try {
            const response = await axiosInstance.get('/inventory/warehouses');
            setWarehouses(response.data);
        } catch (error) {
            console.error("Error fetching warehouses:", error);
            toast.error("Failed to fetch warehouses");
        }
    };

    const handleSaveWarehouse = async () => {
        if (!newWarehouse.name || !newWarehouse.person_in_charge || !newWarehouse.contact_number) {
            toast.error("Please fill in all fields");
            return;
        }

        try {
            await axiosInstance.post('/inventory/warehouses', newWarehouse);
            toast.success("Warehouse added successfully");
            setIsAddWarehouseOpen(false);
            setNewWarehouse({
                name: '',
                person_in_charge: '',
                contact_number: ''
            });
            fetchWarehouses();
        } catch (error) {
            console.error("Error saving warehouse:", error);
            toast.error("Failed to save warehouse");
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewWarehouse(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'machines':
                return <InventoryMachines axiosInstance={axiosInstance} selectedWarehouse={selectedWarehouse} warehouses={warehouses} />;
            case 'consumables':
                return <InventoryConsumables axiosInstance={axiosInstance} selectedWarehouse={selectedWarehouse} warehouses={warehouses} />;
            case 'spare-parts':
                return <InventorySpareParts axiosInstance={axiosInstance} selectedWarehouse={selectedWarehouse} warehouses={warehouses} />;
            case 'demo-unit':
                return <InventoryDemoUnit axiosInstance={axiosInstance} selectedWarehouse={selectedWarehouse} warehouses={warehouses} />;
            case 'accessories':
                return <InventoryAccessories axiosInstance={axiosInstance} selectedWarehouse={selectedWarehouse} warehouses={warehouses} />;
            default:
                return <InventoryMachines axiosInstance={axiosInstance} selectedWarehouse={selectedWarehouse} warehouses={warehouses} />;
        }
    };

    return (
        <div className="p-6 h-full flex flex-col space-y-6">
            <h2 className="text-2xl font-bold">Inventory Management</h2>
            
            <div className="flex-1 grid grid-cols-1 md:grid-cols-[20%_80%] gap-6 overflow-hidden">
                {/* Left Side: Warehouse Management */}
                <div className="bg-white p-4 rounded-lg shadow flex flex-col h-full overflow-hidden">
                    <div className="mb-4">
                        <Button 
                            onClick={() => setIsAddWarehouseOpen(true)} 
                            className="w-full flex items-center justify-center gap-2"
                        >
                            <Plus className="h-4 w-4" />
                            Add Warehouse
                        </Button>
                    </div>

                    <div className="flex-1 overflow-auto">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="font-semibold text-gray-700">Warehouses</h3>
                            {selectedWarehouse && (
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-xs h-6"
                                    onClick={() => setSelectedWarehouse(null)}
                                >
                                    Show All
                                </Button>
                            )}
                        </div>
                        {warehouses.length === 0 ? (
                            <p className="text-sm text-gray-500 text-center py-4">No warehouses found.</p>
                        ) : (
                            <div className="space-y-2">
                                {warehouses.map((warehouse) => (
                                    <div 
                                        key={warehouse.id} 
                                        className={`border p-3 rounded-md hover:bg-gray-50 transition-colors cursor-pointer ${
                                            selectedWarehouse?.id === warehouse.id ? 'bg-orange-50 border-orange-400' : ''
                                        }`}
                                        onClick={() => setSelectedWarehouse(warehouse)}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div>
                                                <h4 className="font-medium text-gray-900">{warehouse.name}</h4>
                                                <p className="text-sm text-gray-500 flex items-center mt-1">
                                                    <span className="font-medium mr-1">PIC:</span> {warehouse.person_in_charge}
                                                </p>
                                                <p className="text-sm text-gray-500 flex items-center">
                                                    <span className="font-medium mr-1">Contact:</span> {warehouse.contact_number}
                                                </p>
                                            </div>
                                            <Building className={`h-4 w-4 ${selectedWarehouse?.id === warehouse.id ? 'text-orange-500' : 'text-gray-400'}`} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Side: Stock Lists Tabs */}
                <div className="bg-white p-4 rounded-lg shadow flex flex-col h-full overflow-hidden">
                    {/* Tabs */}
                    <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-4">
                        <button
                            onClick={() => setActiveTab('machines')}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                activeTab === 'machines'
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200'
                            }`}
                        >
                            Machines
                        </button>
                        <button
                            onClick={() => setActiveTab('consumables')}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                activeTab === 'consumables'
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200'
                            }`}
                        >
                            Consumables
                        </button>
                        <button
                            onClick={() => setActiveTab('spare-parts')}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                activeTab === 'spare-parts'
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200'
                            }`}
                        >
                            Spare Parts
                        </button>
                        <button
                            onClick={() => setActiveTab('demo-unit')}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                activeTab === 'demo-unit'
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200'
                            }`}
                        >
                            Demo Unit
                        </button>
                        <button
                            onClick={() => setActiveTab('accessories')}
                            className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                                activeTab === 'accessories'
                                    ? 'bg-white text-gray-900 shadow-sm'
                                    : 'text-gray-500 hover:text-gray-900 hover:bg-gray-200'
                            }`}
                        >
                            Accessories
                        </button>
                    </div>

                    {/* Tab Content */}
                    <div className="flex-1 overflow-hidden">
                        {renderTabContent()}
                    </div>
                </div>
            </div>

            {/* Add Warehouse Modal */}
            <Dialog open={isAddWarehouseOpen} onOpenChange={setIsAddWarehouseOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Add New Warehouse</DialogTitle>
                        <DialogDescription>
                            Enter the details of the new warehouse.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="name" className="text-right">
                                Name
                            </Label>
                            <Input
                                id="name"
                                name="name"
                                value={newWarehouse.name}
                                onChange={handleInputChange}
                                className="col-span-3"
                                placeholder="Warehouse Name"
                            />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="person_in_charge" className="text-right">
                                PIC
                            </Label>
                            <Input
                                id="person_in_charge"
                                name="person_in_charge"
                                value={newWarehouse.person_in_charge}
                                onChange={handleInputChange}
                                className="col-span-3"
                                placeholder="Person In Charge"
                            />
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="contact_number" className="text-right">
                                Contact
                            </Label>
                            <Input
                                id="contact_number"
                                name="contact_number"
                                value={newWarehouse.contact_number}
                                onChange={handleInputChange}
                                className="col-span-3"
                                placeholder="Contact Number"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="submit" onClick={handleSaveWarehouse}>Save</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Inventory;
